import { notNullOrDie, BigNumber, mustNeverExist } from "@repo/utils";
import type {
    AssistantDecisionKey,
    AssistantDecisions,
    AssistantFieldType,
    AssistantFieldValue,
    PurchaseOrderWorkflowFields,
} from "../../../types.js";
import { compareBigNumbers, compareConfirmations, compareExtraLines, compareScalars } from "./helpers.js";
import type { PurchaseOrderComparison, ScalarComparison } from "./types.js";
import { calculateGrossTotal, calculateNetTotal, calculateNetUnitPrice } from "../../../../erp/calculations.js";
import { handleArbitraryAdjustment } from "../../../po/fields/handleArbitraryFields.js";
import { calculateNumberPrecision } from "../../../../server/shared.js";

const zero = BigNumber(0);

export type ComparisonCorrections = Record<string, AssistantFieldValue<AssistantFieldType>["value"]>;
type ItemComparison = PurchaseOrderComparison["items"][number];
type ItemDatapoints = ItemComparison["datapoints"];

export function adjustPurchaseOrderComparison(
    comparison: PurchaseOrderComparison,
    decisions: AssistantDecisions,
    fields: PurchaseOrderWorkflowFields,
    corrections: ComparisonCorrections,
    context: {
        confirmationDate: string;
        items: Record<
            number,
            {
                priceUnit: BigNumber;
                uomConversionCoefficient: number;
                currencyPrecision: number;
            }
        >;
    },
): PurchaseOrderComparison {
    function retrieveNextValue<T, Type extends AssistantFieldType>(
        type: Type,
        key: AssistantDecisionKey,
        decisionGetter: (value: AssistantFieldValue<Type>["value"], type: Type) => T,
        rejectedGetter: () => T,
    ): { decision: true; value: T } | { decision: false; value: null } {
        const decision = decisions[key];
        if (!decision) {
            return {
                decision: false,
                value: null,
            };
        }

        switch (decision.type) {
            case "accepted":
            case "edited":
                return {
                    decision: true,
                    value: decisionGetter(decision.value.value as AssistantFieldValue<Type>["value"], type),
                };
            case "rejected":
            case "flagged":
            case "ignored":
                return {
                    decision: true,
                    value: rejectedGetter(),
                };
            default:
                throw mustNeverExist(decision);
        }
    }

    function nextScalar<T, Type extends AssistantFieldType>(
        comparison: ScalarComparison<T>,
        type: Type,
        key: AssistantDecisionKey,
        getter: (value: AssistantFieldValue<Type>["value"], type: Type) => T | null,
        comparer: (a: T, b: T) => boolean = (a, b) => a === b,
    ): ScalarComparison<T> {
        const next = retrieveNextValue(type, key, getter, () => comparison.prevValue);

        if (next.decision) {
            return compareScalars(comparison.prevValue, next.value, comparer);
        }

        const correction = corrections[key];

        if (correction) {
            return compareScalars(
                comparison.prevValue,
                getter(correction as AssistantFieldValue<Type>["value"], type),
                comparer,
            );
        }

        return comparison;
    }

    const removed = new Set<number>();

    const items = comparison.items.map<ItemComparison>((item, idx) => {
        if (item.result === "new") {
            if (decisions[`items.${idx}.removed`]) {
                removed.add(idx);
            }
            return item;
        }

        const contextItem = context.items[idx];

        const unitPrice = nextScalar(
            item.datapoints.unitPrice,
            "price",
            `items.${idx}.price`,
            x => notNullOrDie(x.amount).amount,
            compareBigNumbers,
        );

        const quantity = nextScalar(
            item.datapoints.quantity,
            "quantity",
            `items.${idx}.totalQuantity`,
            x => x.amount,
            compareBigNumbers,
        );

        const currencyPrecision = contextItem?.currencyPrecision ?? 2;

        const nextGrossTotal =
            quantity.nextValue !== null || unitPrice.nextValue !== null
                ? calculateGrossTotal(
                      quantity.nextValue ?? quantity.prevValue,
                      unitPrice.nextValue ?? unitPrice.prevValue,
                      contextItem?.priceUnit ?? 1,
                      contextItem?.uomConversionCoefficient ?? 1,
                      currencyPrecision,
                  )
                : null;

        const extra = nextScalar(
            item.datapoints.extra,
            "price",
            `items.${idx}.price`,
            x => x.extra ?? [],
            compareExtraLines,
        );

        const netUnitPrice = compareScalars(
            unitPrice.prevValue === null
                ? null
                : calculateNetUnitPrice(
                      unitPrice.prevValue,
                      quantity.prevValue ?? zero,
                      contextItem?.priceUnit ?? 1,
                      contextItem?.uomConversionCoefficient ?? 1,
                      extra.prevValue ?? [],
                      currencyPrecision,
                  ),
            unitPrice.nextValue === null
                ? null
                : calculateNetUnitPrice(
                      unitPrice.nextValue,
                      quantity.nextValue ?? quantity.prevValue ?? zero,
                      contextItem?.priceUnit ?? 1,
                      contextItem?.uomConversionCoefficient ?? 1,
                      extra.nextValue ?? [],
                      currencyPrecision,
                  ),
            compareBigNumbers,
        );

        const nextNetTotal =
            nextGrossTotal !== null ? calculateNetTotal(nextGrossTotal, extra.nextValue, currencyPrecision) : null;

        const datapoints: ItemDatapoints = {
            unitPrice,
            quantity,
            netUnitPrice,
            supplierSku: nextScalar(item.datapoints.supplierSku, "string", `items.${idx}.supplierSku`, x => x),
            deliveryAddress: nextScalar(item.datapoints.deliveryAddress, "string", "deliveryAddress", x => x),
            scheduleLines: item.datapoints.scheduleLines,
            confirmations: nextScalar(
                item.datapoints.confirmations,
                "scheduleLines",
                `items.${idx}.scheduleLines`,
                x =>
                    x.confirmations.map(x => ({
                        quantity: x.quantity.amount ?? zero,
                        deliveryDate: x.deliveryDate,
                        reference: x.reference,
                    })),
                compareConfirmations,
            ),
            grossTotalPrice: compareScalars(
                item.datapoints.grossTotalPrice.prevValue,
                nextGrossTotal,
                compareBigNumbers,
            ),
            netTotalPrice: compareScalars(item.datapoints.netTotalPrice.prevValue, nextNetTotal, compareBigNumbers),
            extra,
            ignoredExtra: item.datapoints.ignoredExtra,
        };

        if (item.result === "missing") {
            return {
                result: "missing",
                orderId: item.orderId,
                confirmationId: null,
                datapoints,
            };
        }

        return {
            result: item.result,
            orderId: item.orderId,
            confirmationId: item.confirmationId,
            datapoints,
        };
    });

    const currencyPrecision = items.reduce(
        (acc, cur) => calculateNumberPrecision(cur.datapoints.unitPrice.prevValue ?? 0, acc),
        2,
    );

    const nextGrossTotal = items.reduce((acc, item, idx) => {
        if (removed.has(idx)) {
            return acc;
        }
        return acc.plus(
            item.result === "missing"
                ? (item.datapoints.grossTotalPrice.prevValue ?? zero)
                : (item.datapoints.grossTotalPrice.nextValue ?? zero),
        );
    }, zero);

    const cumulativeNetTotal = items.reduce((acc, item, idx) => {
        if (removed.has(idx)) {
            return acc;
        }
        return acc.plus(
            item.result === "missing"
                ? (item.datapoints.netTotalPrice.prevValue ?? zero)
                : (item.datapoints.netTotalPrice.nextValue ?? zero),
        );
    }, zero);

    const extra = nextScalar(comparison.extra, "extra", "headerExtra", x => x.lines, compareExtraLines);

    const adjusted: PurchaseOrderComparison = {
        paymentTerms: nextScalar(comparison.paymentTerms, "registry", "paymentTerms", x => x.id),
        incoterms1: nextScalar(comparison.incoterms1, "deliveryTerms", "deliveryTerms", x => x.incoterms1),
        incoterms2: nextScalar(comparison.incoterms2, "deliveryTerms", "deliveryTerms", x => x.incoterms2),
        items,
        grossTotalPrice: compareScalars(comparison.grossTotalPrice.prevValue, nextGrossTotal, compareBigNumbers),
        netTotalPrice: compareScalars(
            comparison.netTotalPrice.prevValue,
            calculateNetTotal(cumulativeNetTotal, extra.nextValue, currencyPrecision),
            compareBigNumbers,
        ),
        extra,
        arbitrary: { ...comparison.arbitrary },
        ignoredExtra: comparison.ignoredExtra,
    };

    handleArbitraryAdjustment(fields, {
        confirmation: {
            date: context.confirmationDate,
        },
        comparison: adjusted,
        decisions,
    });

    return adjusted;
}
